import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  DATE_TIME_FORMAT,
  FORMAT_TOKENS,
  LOG_COLLECTION_PERIOD,
  SEPARATORS,
  // TIMEZONE_TOKENS,
} from "./allData";
import InfoTooltip from "@components/common/InfoTooltip";
import { X, GripVertical } from "lucide-react";
import { toast } from "@components/ui/use-toast";

type FilterationBlockProps = {
  filterationType?: string;
  onFilterationDataChange?: (
    startKey: string,
    endKey: string,
    customFormat: string,
    logCollectionPeriod: string,
  ) => void;
  // Saved data for prefilling
  savedStartKey?: string;
  savedEndKey?: string;
  savedCustomDateFormat?: string;
  savedLogCollectionPeriod?: string;
};

const FilterationBlock: React.FC<FilterationBlockProps> = ({
  filterationType,
  onFilterationDataChange,
  savedStartKey,
  savedEndKey,
  savedCustomDateFormat,
  savedLogCollectionPeriod,
}) => {
  const [logCollectionPeriod, setLogCollectionPeriod] =
    React.useState<string>("one_hour");
  const [startKey, setStartKey] = React.useState<string>("");
  const [endKey, setEndKey] = React.useState<string>("");
  const [customFormat, setCustomFormat] = React.useState<
    Array<{
      id: string;
      type: "token" | "separator";
      label: string;
      value: string;
      originalId: string; // Track original token id
    }>
  >([]);
  const [draggedItem, setDraggedItem] = React.useState<{
    type: "token" | "separator";
    item: any;
    source?: "palette" | "format"; // Track where item is being dragged from
  } | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const lastRestoredData = React.useRef<string>("");
  const isRestoringRef = React.useRef(false);
  const [currentTime, setCurrentTime] = React.useState(new Date());
  // const [showTimezoneOptions, setShowTimezoneOptions] = React.useState(false);
  const hasInitializedLogPeriod = React.useRef(false);

  // Set log collection period to "one_hour" when filteration type changes from "none" to something else
  React.useEffect(() => {
    // Only auto-set if user hasn't manually changed it and we haven't initialized it yet
    if (
      filterationType &&
      filterationType !== "none" &&
      logCollectionPeriod === "" &&
      !hasInitializedLogPeriod.current &&
      !isRestoringRef.current
    ) {
      setLogCollectionPeriod("one_hour");
      hasInitializedLogPeriod.current = true;
    }
    // Reset the flag when filteration type changes to "none"
    if (!filterationType || filterationType === "none") {
      hasInitializedLogPeriod.current = false;
    }
  }, [filterationType, logCollectionPeriod]);

  // Update time only if custom format includes seconds (ss)
  React.useEffect(() => {
    // Check if custom format includes seconds token
    const hasSeconds = customFormat.some(
      (item) => item.type === "token" && item.originalId === "ss",
    );

    if (!hasSeconds) {
      // No seconds in format, just set time once
      setCurrentTime(new Date());
      return;
    }

    // Has seconds, update every second
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [customFormat]);

  // Restore saved filteration data whenever the saved props change
  React.useEffect(() => {
    // Create a unique signature from all saved props
    const savedDataSignature = JSON.stringify({
      startKey: savedStartKey,
      endKey: savedEndKey,
      logCollectionPeriod: savedLogCollectionPeriod,
      customDateFormat: savedCustomDateFormat,
      filterationType,
    });

    // Skip if we've already restored this exact data
    if (lastRestoredData.current === savedDataSignature) {
      return;
    }

    // Update the ref to prevent re-running for the same data
    lastRestoredData.current = savedDataSignature;
    isRestoringRef.current = true;

    // Set all fields
    setStartKey(savedStartKey || "");
    setEndKey(savedEndKey || "");

    // Set logCollectionPeriod from saved value or empty
    if (savedLogCollectionPeriod) {
      setLogCollectionPeriod(savedLogCollectionPeriod);
      hasInitializedLogPeriod.current = true;
    } else {
      setLogCollectionPeriod("one_hour");
      hasInitializedLogPeriod.current = false;
    }

    // Restore custom format builder if we have a saved custom format and type is custom
    if (
      savedCustomDateFormat &&
      savedCustomDateFormat !== "" &&
      filterationType === "custom"
    ) {
      const parsedFormat = parseCustomDateFormat(savedCustomDateFormat);
      setCustomFormat(parsedFormat);
    } else {
      // Clear custom format if not in custom mode or no saved format
      if (filterationType !== "custom") {
        setCustomFormat([]);
      }
    }

    // Reset restoring flag after a longer delay to ensure all dependent useEffects complete
    // This prevents premature parent notifications during restoration
    const timeoutId = setTimeout(() => {
      isRestoringRef.current = false;

      if (onFilterationDataChangeRef.current && savedCustomDateFormat) {
        onFilterationDataChangeRef.current(
          savedStartKey || "",
          savedEndKey || "",
          savedCustomDateFormat,
          savedLogCollectionPeriod || "one_hour",
        );
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    savedStartKey,
    savedEndKey,
    savedLogCollectionPeriod,
    savedCustomDateFormat,
    filterationType,
  ]);

  // Helper function to parse saved custom date format back to format builder structure
  const parseCustomDateFormat = (
    formatString: string,
  ): Array<{
    id: string;
    type: "token" | "separator";
    label: string;
    value: string;
    originalId: string;
  }> => {
    const result: Array<{
      id: string;
      type: "token" | "separator";
      label: string;
      value: string;
      originalId: string;
    }> = [];

    // Reverse mapping: readable -> python format
    const reverseFormatMap: Record<
      string,
      { value: string; id: string; label: string }
    > = {
      // Date
      YYYY: { value: "%Y", id: "YYYY", label: "YYYY" },
      YY: { value: "%y", id: "YY", label: "YY" },
      MM: { value: "%m", id: "MM", label: "MM" },
      DD: { value: "%d", id: "DD", label: "DD" },

      // Time
      HH: { value: "%H", id: "HH", label: "HH" },
      hh: { value: "%I", id: "hh", label: "hh" },
      mm: { value: "%M", id: "mm", label: "mm" },
      ss: { value: "%S", id: "ss", label: "ss" },
      "AM/PM": { value: "%p", id: "A", label: "AM/PM" },

      // Timezones (VERY IMPORTANT — treated as single tokens)
      UTC: { value: "%Z:UTC", id: "UTC", label: "UTC" },
      GMT: { value: "%Z:GMT", id: "GMT", label: "GMT" },
      EST: { value: "%Z:EST", id: "EST", label: "EST" },
      CST: { value: "%Z:CST", id: "CST", label: "CST" },
      MST: { value: "%Z:MST", id: "MST", label: "MST" },
      PST: { value: "%Z:PST", id: "PST", label: "PST" },
      IST: { value: "%Z:IST", id: "IST", label: "IST" },
      JST: { value: "%Z:JST", id: "JST", label: "JST" },
      AEST: { value: "%Z:AEST", id: "AEST", label: "AEST" },
      CET: { value: "%Z:CET", id: "CET", label: "CET" },
    };

    // Separators
    const separatorMap: Record<string, { label: string; id: string }> = {
      "/": { label: "/", id: "slash" },
      "-": { label: "-", id: "dash" },
      ":": { label: ":", id: "colon" },
      " ": { label: "Space", id: "space" },
      ".": { label: ".", id: "dot" },
      ",": { label: ",", id: "comma" },
    };

    // Sort tokens by length DESC so UTC/AEST match before U/A
    const tokenKeys = Object.keys(reverseFormatMap).sort(
      (a, b) => b.length - a.length,
    );

    let i = 0;
    while (i < formatString.length) {
      let matched = false;

      //  Try matching TOKENS (timezone first because of length sorting)
      for (const key of tokenKeys) {
        if (formatString.startsWith(key, i)) {
          const token = reverseFormatMap[key];
          result.push({
            id: `${token.id}-${Date.now()}-${i}`,
            type: "token",
            label: token.label,
            value: token.value,
            originalId: token.id,
          });
          i += key.length;
          matched = true;
          break;
        }
      }

      if (matched) continue;

      //  Separators
      const char = formatString[i];
      if (separatorMap[char]) {
        const sep = separatorMap[char];
        result.push({
          id: `${sep.id}-${Date.now()}-${i}`,
          type: "separator",
          label: sep.label,
          value: char,
          originalId: sep.id,
        });
        i++;
        continue;
      }

      // Fallback: unknown character → separator
      result.push({
        id: `unknown-${Date.now()}-${i}`,
        type: "separator",
        label: char,
        value: char,
        originalId: "custom",
      });
      i++;
    }

    return result;
  };

  // Get list of available tokens (filter out already used ones)
  const availableTokens = React.useMemo(() => {
    const usedTokenIds = customFormat
      .filter((item) => item.type === "token")
      .map((item) => item.originalId);

    // Check if 12-hour format (hh) is being used
    const hasHourFormat = usedTokenIds.includes("hh");

    return FORMAT_TOKENS.filter((token) => {
      // Hide already used tokens
      if (usedTokenIds.includes(token.id)) return false;

      // Only show AM/PM token if 12-hour format (hh) is present
      if (token.id === "A" && !hasHourFormat) return false;

      return true;
    });
  }, [customFormat]);

  // Get list of available timezones (only allow one timezone in format)
  // const availableTimezones = React.useMemo(() => {
  //   const usedTokenIds = customFormat
  //     .filter((item) => item.type === "token")
  //     .map((item) => item.originalId);

  //   return TIMEZONE_TOKENS.filter((tz) => !usedTokenIds.includes(tz.id));
  // }, [customFormat]);

  const handleDragStart = (
    e: React.DragEvent,
    type: "token" | "separator",
    item: any,
    source: "palette" | "format" = "palette",
  ) => {
    e.dataTransfer.effectAllowed = source === "palette" ? "copy" : "move";
    // Prevent browser from treating this as text drag
    e.dataTransfer.clearData();
    e.dataTransfer.setData(
      "application/x-token",
      JSON.stringify({ type, item, source }),
    );
    setDraggedItem({ type, item, source });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect =
      draggedItem?.source === "palette" ? "copy" : "move";
  };

  const handleDragOverItem = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate if we should insert before or after based on cursor position
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    const isAfter = e.clientX > midpoint;

    setDragOverIndex(isAfter ? index + 0.5 : index - 0.5);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (draggedItem) {
      // If dragging from within the format (reordering)
      if (draggedItem.source === "format") {
        setDraggedItem(null);
        return; // Reordering is handled in handleDropOnItem
      }

      // Check if trying to add a token after another token without a separator
      const lastItem = customFormat[customFormat.length - 1];
      if (draggedItem.type === "token" && lastItem?.type === "token") {
        toast({
          variant: "destructive",
          title: "Please add a separator",
          description: "Please add a separator between date/time tokens.",
          duration: 2000,
        });
        setDraggedItem(null);
        return;
      }

      const newItem = {
        id: `${draggedItem.item.id}-${Date.now()}`,
        type: draggedItem.type,
        label: draggedItem.item.label,
        value: draggedItem.item.value,
        originalId: draggedItem.item.id,
      };
      setCustomFormat([...customFormat, newItem]);
      setDraggedItem(null);
    }
  };

  const handleDropOnItem = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;
    const isAfter = e.clientX > midpoint;
    const insertIndex = isAfter ? targetIndex + 1 : targetIndex;

    setDragOverIndex(null);

    if (!draggedItem) return;

    // Handle reordering from within format
    if (draggedItem.source === "format") {
      const sourceIndex = customFormat.findIndex(
        (item) => item.id === draggedItem.item.id,
      );

      if (sourceIndex === -1) {
        setDraggedItem(null);
        return;
      }

      // Don't do anything if dropping in same position
      if (sourceIndex === insertIndex || sourceIndex === insertIndex - 1) {
        setDraggedItem(null);
        return;
      }

      const newFormat = [...customFormat];
      const [movedItem] = newFormat.splice(sourceIndex, 1);

      // Adjust insert index if we removed an item before it
      const finalInsertIndex =
        sourceIndex < insertIndex ? insertIndex - 1 : insertIndex;
      newFormat.splice(finalInsertIndex, 0, movedItem);

      setCustomFormat(newFormat);
      setDraggedItem(null);
    } else {
      // Handle dropping new item from palette
      // Check if trying to add a token next to another token
      const prevItem = customFormat[insertIndex - 1];
      const nextItem = customFormat[insertIndex];

      if (draggedItem.type === "token") {
        if (prevItem?.type === "token" || nextItem?.type === "token") {
          toast({
            variant: "destructive",
            title: "Please add a separator",
            description: "Please add a separator between date/time tokens.",
            duration: 2000,
          });
          setDraggedItem(null);
          return;
        }
      }

      const newItem = {
        id: `${draggedItem.item.id}-${Date.now()}`,
        type: draggedItem.type,
        label: draggedItem.item.label,
        value: draggedItem.item.value,
        originalId: draggedItem.item.id,
      };

      const newFormat = [...customFormat];
      newFormat.splice(insertIndex, 0, newItem);
      setCustomFormat(newFormat);
      setDraggedItem(null);
    }
  };

  const removeFormatItem = (id: string) => {
    setCustomFormat(customFormat.filter((item) => item.id !== id));
  };

  const clearFormat = () => {
    setCustomFormat([]);
  };

  // Use ref to store the callback to prevent unnecessary re-renders
  const onFilterationDataChangeRef = React.useRef(onFilterationDataChange);
  React.useEffect(() => {
    onFilterationDataChangeRef.current = onFilterationDataChange;
  }, [onFilterationDataChange]);

  // Track previous values to avoid unnecessary calls
  // Initialize with saved values to prevent false change detection during restoration
  const prevValuesRef = React.useRef({
    startKey: savedStartKey || "",
    endKey: savedEndKey || "",
    formatString: savedCustomDateFormat || "",
    logCollectionPeriod: savedLogCollectionPeriod || "one_hour",
  });

  // Update prevValuesRef when saved props change to stay in sync during restoration
  React.useEffect(() => {
    if (isRestoringRef.current) {
      prevValuesRef.current = {
        startKey: savedStartKey || "",
        endKey: savedEndKey || "",
        formatString: savedCustomDateFormat || "",
        logCollectionPeriod: savedLogCollectionPeriod || "one_hour",
      };
    }
  }, [
    savedStartKey,
    savedEndKey,
    savedCustomDateFormat,
    savedLogCollectionPeriod,
  ]);

  // Notify parent of filteration data changes
  React.useEffect(() => {
    // Don't notify parent during restoration to prevent loops
    if (isRestoringRef.current) {
      return;
    }

    // Convert Python format codes to readable format (YYYY/MM/DD hh:mm:ss AM/PM)
    const formatString = customFormat
      .map((item) => {
        if (item.type === "separator") {
          return item.value;
        }
        // Convert Python format codes to readable format
        // Save as "AM/PM" for the AM/PM indicator
        const formatMap: Record<string, string> = {
          "%Y": "YYYY",
          "%y": "YY",
          "%m": "MM",
          "%d": "DD",
          "%H": "HH",
          "%I": "hh",
          "%M": "mm",
          "%S": "ss",
          "%Z:UTC": "UTC",
          "%Z:GMT": "GMT",
          "%Z:EST": "EST",
          "%Z:CST": "CST",
          "%Z:MST": "MST",
          "%Z:PST": "PST",
          "%Z:IST": "IST",
          "%Z:JST": "JST",
          "%Z:AEST": "AEST",
          "%Z:CET": "CET",
          "%p": "AM/PM", // Save as "AM/PM"
        };
        return formatMap[item.value] || item.value;
      })
      .join("");

    // Only call parent if values have actually changed
    const hasChanged =
      prevValuesRef.current.startKey !== startKey ||
      prevValuesRef.current.endKey !== endKey ||
      prevValuesRef.current.formatString !== formatString ||
      prevValuesRef.current.logCollectionPeriod !== logCollectionPeriod;

    if (hasChanged && onFilterationDataChangeRef.current) {
      prevValuesRef.current = {
        startKey,
        endKey,
        formatString,
        logCollectionPeriod,
      };
      onFilterationDataChangeRef.current(
        startKey,
        endKey,
        formatString,
        logCollectionPeriod,
      );
    }
  }, [startKey, endKey, customFormat, logCollectionPeriod]);

  const getPreviewFormat = () => {
    return customFormat
      .map((item) => {
        // For space separator, show actual space instead of the word "Space"
        if (item.type === "separator" && item.label === "Space") {
          return " ";
        }
        // Show AM/PM in the preview instead of just displaying the label
        if (item.type === "token" && item.value === "%p") {
          return "AM/PM";
        }
        return item.label;
      })
      .join("");
  };

  const getFormattedPreview = () => {
    const now = currentTime;
    let formatted = "";

    customFormat.forEach((item) => {
      if (item.type === "separator") {
        formatted += item.value;
      } else {
        // Convert Python format to actual value
        const hours12 = now.getHours() % 12 || 12;
        switch (item.value) {
          case "%Y":
            formatted += now.getFullYear().toString();
            break;
          case "%y":
            formatted += now.getFullYear().toString().slice(-2);
            break;
          case "%m":
            formatted += String(now.getMonth() + 1).padStart(2, "0");
            break;
          case "%d":
            formatted += String(now.getDate()).padStart(2, "0");
            break;
          case "%H":
            formatted += String(now.getHours()).padStart(2, "0");
            break;
          case "%I":
            formatted += String(hours12).padStart(2, "0");
            break;
          case "%M":
            formatted += String(now.getMinutes()).padStart(2, "0");
            break;
          case "%S":
            formatted += String(now.getSeconds()).padStart(2, "0");
            break;
          case "%p":
            formatted += now.getHours() >= 12 ? "PM" : "AM";
            break;
          case "%Z:UTC":
            formatted += "UTC";
            break;
          case "%Z:GMT":
            formatted += "GMT";
            break;
          case "%Z:EST":
            formatted += "EST";
            break;
          case "%Z:CST":
            formatted += "CST";
            break;
          case "%Z:MST":
            formatted += "MST";
            break;
          case "%Z:PST":
            formatted += "PST";
            break;
          case "%Z:IST":
            formatted += "IST";
            break;
          case "%Z:JST":
            formatted += "JST";
            break;
          case "%Z:AEST":
            formatted += "AEST";
            break;
          case "%Z:CET":
            formatted += "CET";
            break;
          default:
            formatted += item.label;
        }
      }
    });

    return formatted;
  };

  // TODO : NEEDED IN THE FUTURE
  // Check if a timezone token is already in the custom format
  // const hasTimezone = customFormat.some((item) =>
  //   TIMEZONE_TOKENS.map((t) => t.id).includes(item.originalId),
  // );

  // //resetTimezone
  // const resetTimezone = () => {
  //   const newFormat = customFormat.filter((item) => {
  //     return !TIMEZONE_TOKENS.map((t) => t.id).includes(item.originalId);
  //   });
  //   setCustomFormat(newFormat);
  // };

  return (
    <div className="mt-3">
      <p className="text-primaryGreen font-bold mb-1 ml-1">Filteration</p>
      <div className="p-3 rounded-lg border bg-lightGray border-white/50">
        <p className="text-muted-foreground border-b pb-2 mb-2 px-1 ">
          {" "}
          Selected Filteration Type :{" "}
          <strong>
            {DATE_TIME_FORMAT.find((m) => m.value === filterationType)?.label ||
              ""}
          </strong>{" "}
        </p>
        <div className="backdrop-blur-md py-1">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr] gap-6 items-start">
            {/* Start Key */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted-foreground ml-1">
                Start Key
              </label>
              <input
                type="text"
                value={startKey}
                onChange={(e) => setStartKey(e.target.value)}
                className="h-10 rounded-xl border border-lightGray px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue/30"
                placeholder="Enter start key"
              />
            </div>

            {/* End Key */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted-foreground ml-1">
                End Key
              </label>
              <input
                type="text"
                value={endKey}
                onChange={(e) => setEndKey(e.target.value)}
                className="h-10 rounded-xl border border-lightGray px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primaryBlue/30"
                placeholder="Enter end key"
              />
            </div>

            {/* Log Collection Period */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted-foreground ml-1">
                  Log collection period
                  <span className="ml-1 font-normal">(optional)</span>
                </label>
                <InfoTooltip text="If none of the items were selected, the logs will be collected for the last 1 hour." />
              </div>

              <Select
                value={logCollectionPeriod}
                defaultValue="one_hour"
                onValueChange={(v) => {
                  setLogCollectionPeriod(v);
                }}
              >
                <SelectTrigger className="h-10 rounded-xl border-lightGray">
                  <SelectValue placeholder="Select log collection period" />
                </SelectTrigger>

                <SelectContent className="rounded-xl">
                  <SelectItem value="none">
                    Choose Log collection period
                  </SelectItem>
                  {LOG_COLLECTION_PERIOD.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Custom DateTime Format Builder */}
        {filterationType === "custom" ? (
          <div className="mt-4 p-3 pb-5 bg-background rounded-xl border w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-primaryGreen">
                Custom DateTime Format Builder
              </h3>
              {customFormat.length > 0 && (
                <button
                  onClick={clearFormat}
                  className="text-xs font-semibold text-primary"
                >
                  Clear All
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Side - Available Tokens */}
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-xs font-medium mb-2 text-muted-foreground">
                    Date & Time Tokens (Drag to use)
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-4 border-2 p-2 min-h-[120px] rounded-2xl ">
                    {availableTokens.length > 0 ? (
                      availableTokens.map((token) => (
                        <div
                          key={token.id}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, "token", token)
                          }
                          className="px-1 py-1 border border-dashed shadow border-lightGray rounded-lg cursor-move h-fit select-none hover:scale-105 transition-all"
                          title={token.description}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-primaryBlue">
                              {token.label}
                            </span>
                            <GripVertical
                              size={14}
                              className="text-muted-foreground"
                            />
                          </div>
                          <p className="text-[10px] text-[#82a1d3] mt-1 truncate">
                            {token.description}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-4 text-center text-sm text-muted-foreground py-4">
                        All tokens have been used
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium mb-2 text-muted-foreground">
                    Separators (Drag to use)
                  </p>
                  <div className="grid grid-cols-6 gap-2">
                    {SEPARATORS.map((sep) => (
                      <div
                        key={sep.id}
                        draggable
                        onDragStart={(e) =>
                          handleDragStart(e, "separator", sep)
                        }
                        className="p-2 border border-lightGray rounded-lg shadow-md cursor-move hover:bg-lightGray transition-colors text-center"
                      >
                        <span className="text-sm font-mono font-semibold">
                          {sep.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* TODO : NEEDED IN THE FUTURE   */}

                {/* show/hide Timezone checkbox */}

                {/* <div className="flex itmes-center gap-2 p-2">
                  <input
                    type="checkbox"
                    checked={showTimezoneOptions}
                    onChange={() =>
                      setShowTimezoneOptions(!showTimezoneOptions)
                    }
                    id="toggle_show_timezone"
                  />
                  <label htmlFor="toggle_show_timezone">Time zones</label>
                </div> */}

                {/* Timezones secion  */}
                {/* {showTimezoneOptions && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 ">
                        Time zone ({" "}
                        {!hasTimezone ? (
                          "Drag to use"
                        ) : (
                          <div className="font-bold">
                            {
                              customFormat.find((item) =>
                                TIMEZONE_TOKENS.map((t) => t.id).includes(
                                  item.originalId,
                                ),
                              )?.label
                            }
                            <span className="font-normal ml-1">
                              has already been added
                            </span>
                          </div>
                        )}
                        )
                      </p>
                      {hasTimezone && (
                        <button
                          className="text-xs text-primary"
                          onClick={resetTimezone}
                        >
                          Reset Timezone
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 border-2 p-2 min-h-[80px] rounded-xl">
                      {availableTimezones.length > 0 ? (
                        availableTimezones.map((tz) => (
                          <div
                            key={tz.id}
                            draggable={!hasTimezone}
                            onDragStart={(e) => {
                              if (hasTimezone) {
                                e.preventDefault();
                                return;
                              }
                              handleDragStart(e, "token", tz);
                            }}
                            className={`px-1 py-1 border border-dashed shadow rounded-md select-none transition-all h-fit ${
                              hasTimezone
                                ? "opacity-40 cursor-not-allowed bg-gray-100 border-gray-300"
                                : "cursor-move hover:scale-105"
                            }`}
                            title={
                              hasTimezone
                                ? "Only one timezone allowed"
                                : tz.description
                            }
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-sm font-bold ${
                                  hasTimezone ? "text-gray-500" : "text-amber"
                                }`}
                              >
                                {tz.label}
                              </span>
                              <GripVertical
                                size={14}
                                className="text-muted-foreground"
                              />
                            </div>
                            <p
                              className={`text-[10px] mt-1 truncate ${
                                hasTimezone
                                  ? "text-gray-500"
                                  : "text-primaryAmber"
                              }`}
                            >
                              {tz.description}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-4 text-center text-sm text-muted-foreground py-4">
                          Timezone is in use
                        </div>
                      )}
                    </div>
                  </div>
                )} */}
              </div>

              {/* Right Side - Drop Zone */}
              <div>
                <p className="text-xs font-medium mb-2 text-muted-foreground">
                  Your Custom Format (Drop here)
                </p>
                <div
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`min-h-[180px] p-3 border-2 border-dashed rounded-2xl hover:border-muted-foreground${
                    customFormat.length === 0
                      ? " bg-muted flex items-center justify-center"
                      : " border-gray-300"
                  }`}
                >
                  {customFormat.length === 0 ? (
                    <div className="flex flex-col gap-1 items-center justify-center h-full text-muted-foreground text-sm">
                      <span className="text-xs">
                        Drag and drop Tokens and Separators here to build your
                        format
                      </span>
                      <span>
                        eg :{" "}
                        <code className="bg-background p-1 px-2 rounded">
                          YYYY/MM/DD hh:mm:ss
                        </code>
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 items-center">
                      {customFormat.map((item, index) => (
                        <React.Fragment key={item.id}>
                          {/* Insertion indicator before item */}
                          {dragOverIndex !== null &&
                            Math.floor(dragOverIndex) === index &&
                            dragOverIndex < index + 0.5 && (
                              <div className="w-0.5 h-8 bg-primaryBlue rounded-full animate-pulse shadow-lg shadow-primaryBlue/50" />
                            )}

                          <div
                            draggable
                            onDragStart={(e) =>
                              handleDragStart(e, item.type, item, "format")
                            }
                            onDragOver={(e) => handleDragOverItem(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDropOnItem(e, index)}
                            className={`flex items-center gap-1 px-2 py-1.5 cursor-move transition-all duration-200 rounded-full shadow-sm ${
                              item.type === "token"
                                ? "bg-blue-100 border border-blue-300 hover:bg-blue-200 hover:shadow-md"
                                : "border border-gray-300 hover:bg-gray-200 hover:shadow-md"
                            } ${
                              draggedItem?.source === "format" &&
                              draggedItem.item.id === item.id
                                ? "opacity-50 scale-95"
                                : "opacity-100 scale-100"
                            }`}
                          >
                            <GripVertical
                              size={13}
                              className="text-muted-foreground hover:text-primary transition-colors"
                            />
                            <span className="text-sm font-mono font-semibold select-none">
                              {item.label}
                            </span>
                            <button
                              onClick={() => removeFormatItem(item.id)}
                              className="ml-1 text-primary hover:text-red-600 transition-colors hover:scale-110"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>

                          {/* Insertion indicator after item */}
                          {dragOverIndex !== null &&
                            Math.floor(dragOverIndex) === index &&
                            dragOverIndex >= index + 0.5 && (
                              <div className="w-0.5 h-8 bg-primaryBlue rounded-full animate-pulse shadow-lg shadow-primaryBlue/50" />
                            )}
                        </React.Fragment>
                      ))}

                      {/* Insertion indicator at the end */}
                      {dragOverIndex !== null &&
                        dragOverIndex >= customFormat.length && (
                          <div className="w-0.5 h-8 bg-primaryBlue rounded-full animate-pulse shadow-lg shadow-primaryBlue/50" />
                        )}
                    </div>
                  )}
                </div>

                {/* Preview Section */}
                {customFormat.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3 p-3 bg-lightBlue/40 rounded-xl border border-lightGray shadow-md">
                      <div>
                        <p className="text-xs font-semibold text-primary mb-1">
                          Format Structure
                        </p>
                        <p className="text-sm font-mono text-muted-foreground bg-muted p-2 rounded-lg border">
                          {getPreviewFormat()}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">
                          Preview Output
                        </p>
                        <p className="text-sm font-mono text-muted-foreground bg-muted p-2 rounded-lg border">
                          {getFormattedPreview()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FilterationBlock;
